/**
 * GalleryEditor — upload, reorder and remove up to MAX_PHOTOS gallery images.
 * Uses the same /uploads/image endpoint as ImageUpload.
 */
import { useRef, useState } from "react";
import { Plus, X, Loader2, GripVertical, ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MAX_PHOTOS = 8;

export default function GalleryEditor({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const photos = value.filter(Boolean);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    const toUpload = files.slice(0, remaining);
    if (!toUpload.length) { toast.error(`Maximum ${MAX_PHOTOS} photos allowed`); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...photos, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  // Drag-to-reorder
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); setOverIdx(i); };
  const onDrop = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const next = [...photos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    onChange(next);
    setDragIdx(null); setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div data-testid="gallery-editor">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((url, i) => (
          <div
            key={url + i}
            draggable
            onDragStart={() => onDragStart(i)}
            onDragOver={(e) => onDragOver(e, i)}
            onDrop={(e) => onDrop(e, i)}
            onDragEnd={onDragEnd}
            data-testid={`gallery-photo-${i}`}
            className={`relative group aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-grab active:cursor-grabbing
              ${overIdx === i && dragIdx !== i ? "border-[#144F75] scale-[1.03]" : "border-[#E5E0D8]"}`}
          >
            <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
            {/* Drag handle */}
            <div className="absolute top-1 left-1 bg-black/40 rounded p-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
              <GripVertical size={12} className="text-white" />
            </div>
            {/* Remove button */}
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`gallery-remove-${i}`}
              className="absolute top-1 right-1 bg-black/50 hover:bg-red-600 text-white rounded-full w-6 h-6 grid place-items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all"
            >
              <X size={12} />
            </button>
            <div className="absolute bottom-0 inset-x-0 h-6 bg-gradient-to-t from-black/30 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" />
          </div>
        ))}

        {/* Upload slot */}
        {photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="gallery-add-btn"
            className="aspect-square rounded-xl border-2 border-dashed border-[#E5E0D8] hover:border-[#144F75] hover:bg-[#144F75]/5 grid place-items-center text-[#8FA89B] hover:text-[#144F75] transition-all disabled:opacity-50"
          >
            {uploading ? <Loader2 size={20} className="animate-spin" /> : (
              <div className="flex flex-col items-center gap-1">
                <Plus size={20} />
                <span className="text-[10px] font-medium">Add photo</span>
              </div>
            )}
          </button>
        )}
      </div>

      {photos.length === 0 && !uploading && (
        <div className="mt-2 flex items-center gap-2 text-xs text-[#8FA89B]">
          <ImageIcon size={13} /> No photos yet — click "Add photo" to upload up to {MAX_PHOTOS} images
        </div>
      )}

      {photos.length > 0 && (
        <p className="mt-2 text-xs text-[#8FA89B]">{photos.length} / {MAX_PHOTOS} photos · drag to reorder</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
      />
    </div>
  );
}
